import { useState, useMemo, useEffect } from 'react'

import { Text } from '@interco/inter-ui/components/Text'
import ChevronDown from '@interco/icons/core/action-navigation/ic_chevron_down'
import ChevronUp from '@interco/icons/core/action-navigation/ic_chevron_up'
import Search from '@interco/icons/core/action-navigation/ic_search'
import SolidClose from '@interco/icons/core/action-navigation/ic_close'
import { Page } from '@templates'
import { Colors } from '@utils/Colors'
import { RemoveAccents } from '@interco/inter-ui-react-lib'
import { openLinkByEnviroment } from '@utils/functions'
import useTrackingRefin from '@hooks/useTrackingRefin'
import { StepRefin } from '@utils/enums'

import { faqData, FAQItem } from './faqData'
import * as S from './styles'

const FAQ = () => {
  useTrackingRefin(StepRefin.FAQ)

  const [searchTerm, setSearchTerm] = useState('')

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  useEffect(() => {
    document.body.classList.add('faq-page')
    return () => {
      document.body.classList.remove('faq-page')
    }
  }, [])

  const isEmptySearch = !searchTerm.trim()

  const clearSearch = () => {
    setSearchTerm('')
  }

  const filteredFAQs = useMemo(() => {
    if (!searchTerm.trim()) {
      return faqData
    }

    const normalizedSearch = RemoveAccents(searchTerm.trim().toLowerCase())

    return faqData.filter((item) => {
      const normalizedQuestion = RemoveAccents(item.question.toLowerCase())
      const normalizedAnswer = Array.isArray(item.answer)
        ? item.answer.map((a) => RemoveAccents(a.toLowerCase())).join(' ')
        : RemoveAccents(item.answer.toLowerCase())

      return (
        normalizedQuestion.includes(normalizedSearch) || normalizedAnswer.includes(normalizedSearch)
      )
    })
  }, [searchTerm])

  const toggleItem = (id: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedItems(newExpanded)
  }

  const handleLinkClick = async (url: string, event: React.MouseEvent) => {
    event.preventDefault()
    await openLinkByEnviroment(url)
  }

  const renderTextWithLinks = (text: string) => {
    const linkMap: Record<string, string> = {
      'carteira assinada': 'https://blog.inter.co/mei-pode-ter-carteira-assinada/',

      'margem consignável':
        'https://blog.inter.co/o-que-e-margem-consignavel-do-emprestimo-consignado/',
    }
    const linkWords = Object.keys(linkMap)
    const parts: (string | JSX.Element)[] = []
    const remainingText = text
    let keyIndex = 0

    const allMatches: Array<{ index: number; word: string; length: number; url: string }> = []

    linkWords.forEach((word) => {
      const regex = new RegExp(word, 'gi')
      const matches = [...remainingText.matchAll(regex)]
      matches.forEach((match) => {
        if (match.index !== undefined) {
          allMatches.push({
            index: match.index,
            word: match[0],
            length: match[0].length,
            url: linkMap[word],
          })
        }
      })
    })

    allMatches.sort((a, b) => a.index - b.index)

    let lastIndex = 0
    allMatches.forEach((match) => {
      if (match.index > lastIndex) {
        parts.push(remainingText.substring(lastIndex, match.index))
      }
      parts.push(
        <S.AnswerLink
          key={`link-${keyIndex}`}
          onClick={(e) => handleLinkClick(match.url, e)}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleLinkClick(match.url, e as unknown as React.MouseEvent)
            }
          }}
          aria-label={`Abrir ${match.word} em navegador externo`}
        >
          {match.word}
        </S.AnswerLink>,
      )
      keyIndex += 1
      lastIndex = match.index + match.length
    })

    if (lastIndex < remainingText.length) {
      parts.push(remainingText.substring(lastIndex))
    }

    return parts.length > 0 ? parts : text
  }

  const renderAnswer = (answer: string | string[], faqId: string) => {
    if (Array.isArray(answer)) {
      return (
        <S.AnswerList>
          {answer.map((item, index) => (
            <S.AnswerItem key={`${faqId}-${item}`} $isFirstItem={index === 0}>
              <Text variant="caption-1" colorWeight={400}>
                {renderTextWithLinks(item)}
              </Text>
            </S.AnswerItem>
          ))}
        </S.AnswerList>
      )
    }

    return (
      <Text
        variant="caption-1"
        colorWeight={400}
        style={answer.includes('\n') ? { whiteSpace: 'pre-wrap' } : undefined}
      >
        {renderTextWithLinks(answer)}
      </Text>
    )
  }

  return (
    <>
      <S.GlobalFAQStyles />
      <Page titleHeader="Perguntas frequentes" showLeftIcon>
        <S.Container>
          <S.SearchContainer>
            <S.SearchInputWrapper>
              {isEmptySearch && (
                <S.SearchIcon>
                  <Search width={28} height={28} color={Colors.PRIMARY500} />
                </S.SearchIcon>
              )}

              <S.CustomInput
                id="faq-search"
                type="text"
                label=""
                placeholder="Pesquisar"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                dataTestId="faq-search"
                iconRight={
                  !isEmptySearch ? (
                    <SolidClose width={20} height={20} color={Colors.PRIMARY500} />
                  ) : undefined
                }
                onClickIconRight={!isEmptySearch ? clearSearch : undefined}
              />
            </S.SearchInputWrapper>
          </S.SearchContainer>

          <S.FAQList>
            {filteredFAQs.length === 0 ? (
              <S.EmptyState>
                <Text variant="body-3" colorWeight={400} style={{ color: Colors.GRAY400 }}>
                  Nenhuma dúvida encontrada. Tente pesquisar com outros termos.
                </Text>
              </S.EmptyState>
            ) : (
              filteredFAQs.map((item: FAQItem) => {
                const isExpanded = expandedItems.has(item.id)

                return (
                  <S.FAQItem key={item.id}>
                    <S.QuestionButton
                      type="button"
                      onClick={() => toggleItem(item.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`answer-${item.id}`}
                    >
                      <S.QuestionText>
                        <Text variant="body-3" bold colorWeight={500}>
                          {item.question}
                        </Text>
                      </S.QuestionText>
                      {isExpanded ? (
                        <ChevronUp width={24} height={24} color={Colors.PRIMARY500} />
                      ) : (
                        <ChevronDown width={24} height={24} color={Colors.PRIMARY500} />
                      )}
                    </S.QuestionButton>

                    {isExpanded && (
                      <S.AnswerContainer id={`answer-${item.id}`}>
                        {renderAnswer(item.answer, item.id)}
                      </S.AnswerContainer>
                    )}
                  </S.FAQItem>
                )
              })
            )}
          </S.FAQList>
        </S.Container>
      </Page>
    </>
  )
}

export default FAQ
